import React, { FunctionComponent, useMemo } from 'react'
import { formatStringFr, getAge, getAgeWithUnit } from '../../../../misc/date.utilities'
import { Icon, Tag, TagCollection, TooltipWrapper } from '../../../shared'
import { PatientDrugFormated, PatientListItemProps } from './PatientListItem.model'
import { SEX_LABELS, Sex, MIN_MAJOR_AGE } from '../../../../model/Patient'
import { getV3ColorCssVariable } from '../../../../design-system/colors_v3'
import { getFullName } from '../../../../misc/user.utilities'
import RoleRestriction from '../../../../containers/RoleRestriction'
import { ADMIN_ROLES } from '../../../../model/Roles'
import { getPriorityNames } from '../../../../misc/patient.utilities'
import classNames from 'classnames'
import { ColorsV2TagBackground } from '../../../../design-system/colors_v2'
import styles from './PatientListItem.module.scss'

export const PatientListItem: FunctionComponent<PatientListItemProps> = ({
  patient,
  currentUser,
}) => {
  const { primaryFirstName, primaryLastName, secondaryFirstName, secondaryLastName } =
    getPriorityNames(patient)

  const patientDrugsFormated: Array<PatientDrugFormated> = useMemo(() => {
    if (patient.drugs.length < 3) {
      return patient.drugs.map(({ name }) => ({ title: name, label: name }))
    } else {
      return [
        {
          title: `${patient.drugs.length} MÉDICAMENTS`,
          label: (
            <>
              {patient.drugs.map(({ name, signature }) => (
                <span key={signature}>{name}</span>
              ))}
            </>
          ),
        },
      ]
    }
  }, [patient.drugs])

  const displayNameExtra = useMemo(() => {
    const hasDifferentSecondaryIdentity =
      secondaryFirstName &&
      secondaryLastName &&
      (secondaryFirstName !== primaryFirstName || secondaryLastName !== primaryLastName)

    if (!hasDifferentSecondaryIdentity) return ''

    return ` (né.e ${secondaryFirstName} ${secondaryLastName})`
  }, [primaryFirstName, primaryLastName, secondaryFirstName, secondaryLastName])

  return (
    <div className={styles.item}>
      <div className={styles.visibility}>
        <div title={SEX_LABELS[patient.sex]}>
          {patient.sex !== Sex.UNKNOWN && (
            <Icon
              icon={patient.sex === Sex.MALE ? 'male' : 'female'}
              color={getV3ColorCssVariable('shades', 'shade2')}
              size="normal"
            />
          )}
        </div>
      </div>
      <div className={styles.titleContainer}>
        <div className={styles.titleRow}>
          <span
            className={classNames(styles.title, { [styles.titleDeceased]: patient.isDeceased })}
          >
            {primaryFirstName} <strong>{primaryLastName}</strong>
            {displayNameExtra}
          </span>
          <div
            className={classNames(styles.age, { [styles.titleThrough]: patient.isDeceased })}
            title={formatStringFr(patient.birthDate, 'dd MMMM yyyy')}
          >
            <span className={styles.ageLabel}>{getAgeWithUnit(patient.birthDate)} </span>
            <span className={styles.birthdateLabel}>
              ({formatStringFr(patient.birthDate, 'd MMMM yyyy')})
            </span>
          </div>
        </div>
        <div className={styles.deceasedAndMinorLabelContainer}>
          {getAge(patient.birthDate) < MIN_MAJOR_AGE && (
            <Tag color={ColorsV2TagBackground.dsTag03} testId="patient-list_item-minor">
              Mineur·e
            </Tag>
          )}
          {patient.isDeceased && (
            <Tag color={ColorsV2TagBackground.dsTag01} testId="patient-list_item-minor">
              Décédé·e
            </Tag>
          )}
        </div>
        {patient.owner.id !== currentUser?.id && (
          <div className={styles.ownerName}>{getFullName(patient.owner, true)}</div>
        )}
      </div>
      {patient.drugs.length > 0 && (
        <div className={styles.drugsContainer}>
          {patientDrugsFormated.map((drug, index) => {
            return (
              <TooltipWrapper
                key={index}
                content={<div className={styles.drugNameTooltip}>{drug.label}</div>}
                pointerDirection="top"
              >
                <Tag ellipsis={false} testId="patient-drug_name">
                  <div className={styles.drugNameWithIcon}>
                    <Icon icon="pills" size="nano" />
                    <span className={styles.drugName}>{drug.title}</span>
                  </div>
                </Tag>
              </TooltipWrapper>
            )
          })}
        </div>
      )}
      <div className={styles.tags}>
        <TagCollection ids={patient.tagIds} />
      </div>
      <RoleRestriction roles={ADMIN_ROLES}>
        <div className={styles.identifier} title="Identifiant technique du patient">
          {patient.id}
        </div>
      </RoleRestriction>
    </div>
  )
}
